import { RolloutMonitoring, OrgOverviewStatistic, ReleaseStep, OrgUser } from './../types';
import { AppEntity, AppGroup, IntegrationProvider, Release } from '../types';
import { StringKeyedDictionary } from '../typescriptHelpers';
import { AppsAddedSuccessAction } from './app';
import { BuildDistroReducerType } from '../reducers/buildDistribution';
import { CommonSagaActionsData } from '@/actions/entities';
import type { OrgInsightsResponse } from '@/actions/entities';
import { StepRun } from '@/types/stepRun';
import { StepConfig } from '@/types/stepConfig';
import { OrgDestinationsResponse, OrgSummaryPreferences, SummaryViewResponseAPI } from '@/types/summaryView';
import { UpdateIssuesPaginationAction } from './triage';

export interface ReleaseDemoData {
    release: Release;
}

export interface ReleaseStepDemoData {
    releaseStep: ReleaseStep;
}

export type PaginationDataMap<T = object> = Record<
    string,
    T & {
        limit: number;
        offset: number;
        totalCount?: number;
    }
>;

export interface AppDemoData {
    app: AppEntity;
    releaseIds: string[];
}

export interface DemoData {
    releaseDemoData?: StringKeyedDictionary<ReleaseDemoData>;
    workflowRunSidebarDataById?: StringKeyedDictionary<ReleaseStepDemoData>;
    releaseStepsDemoData?: StringKeyedDictionary<ReleaseStepDemoData>;
    appsDemoData?: StringKeyedDictionary<AppDemoData>;
    appGroupsDemoData?: AppGroup[];
    integrationProviders?: IntegrationProvider[];
    statistics?: Record<string, OrgOverviewStatistic[]>;
    buildDistroDemoData?: BuildDistroReducerType;
    stepRunsDemoData?: StringKeyedDictionary<StepRun>;
    stepConfigsDemoData?: StringKeyedDictionary<StepConfig>;
    orgDestinations?: Record<string, OrgDestinationsResponse>;
    orgInsights?: Record<string, OrgInsightsResponse>;
    summaryPreferences?: Record<string, OrgSummaryPreferences>;
    summaryViewByRequestId?: Record<string, SummaryViewResponseAPI>;
    orgUsersDemoData?: StringKeyedDictionary<OrgUser>;
}

export interface WelcomeModalOptions {
    shouldShowWelcomeModal: boolean;
    shouldDirectlyShowNewAppFlow?: boolean;
}

export interface EventsTimelineDrawerState {
    stepRunId: string | null;
    isVisible: boolean;
}

export interface UiConfig {
    runwayAdminLiveModeEnabled: boolean;
    runwayAdminWebsocketsEnabled: boolean;
    welcomeModalOptions: WelcomeModalOptions;
    appsAddedSuccess: string[]; // array of appIdParams for newly added apps
    demoData?: DemoData;
    releasesPagination: PaginationDataMap;
    bucketBuildsPagination: PaginationDataMap;
    sharedWithMeBuildsPagination: PaginationDataMap;
    bucketBuildsWorkItemsTimelinePagination: PaginationDataMap;
    issuesPagination: PaginationDataMap;

    // Since pagination data will be keyed by entity type + id, we can use one map to store all pagination data. I think this should be the case from now on on any endpoints using RunwayResponseV2
    paginationData: PaginationDataMap;

    // Events timeline drawer state
    eventsTimelineDrawer: EventsTimelineDrawerState;
}

export const UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED = 'UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED';
export const UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED = 'UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED';
export const SET_DEMO_RELEASES = 'SET_DEMO_RELEASES';
export const CLEAR_RELEASE_DEMO_MODE = 'CLEAR_RELEASE_DEMO_MODE';
export const SET_APPS_DEMO_MODE = 'SET_APPS_DEMO_MODE';
export const CLEAR_APPS_DEMO_MODE = 'CLEAR_APPS_DEMO_MODE';
export const SET_SHOW_WELCOME_MODAL = 'SET_SHOW_WELCOME_MODAL';
export const SET_SHOW_ADD_APPS = 'SET_SHOW_ADD_APPS';
export const UPDATE_RELEASES_PAGINATION = 'UPDATE_RELEASES_PAGINATION';
export const UPDATE_BUCKET_BUILDS_PAGINATION = 'UPDATE_BUCKET_BUILDS_PAGINATION';
export const UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION = 'UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION';
export const UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION = 'UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION';
export const APPS_ADDED_SHOULD_REDIRECT = 'APPS_ADDED_SHOULD_REDIRECT';
export const UPDATE_ALL_DEVICES_PAGINATION = 'UPDATE_ALL_DEVICES_PAGINATION';

export const UPDATE_PAGINATION_DATA = 'UPDATE_PAGINATION_DATA'; // new way to handle pagination data

export const SET_EVENTS_TIMELINE_DRAWER_VISIBILITY = 'SET_EVENTS_TIMELINE_DRAWER_VISIBILITY';

interface UpdateUIConfigAction {
    type: typeof UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED;
    payload: {
        enabled: boolean;
    };
}

interface UpdateUIConfigWebsocketsEnabledAction {
    type: typeof UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED;
    payload: {
        enabled: boolean;
    };
}

export type SetWelcomeModalOptions = WelcomeModalOptions;

export interface UpdateReleasesPaginationPayload {
    appId: string;
    limit: number;
    offset: number;
    currentCount: number;
    totalCount: number;
}

export interface UpdateBucketBuildsPaginationPayload {
    bucketId: string;
    limit: number;
    offset: number;
    currentCount: number;
}

export interface UpdateSharedWithMeBuildsPaginationPayload {
    appId: string;
    limit: number;
    offset: number;
    currentCount: number;
    totalCount: number;
}

export interface UpdateBucketBuildWorkItemsTimelinePaginationPayload {
    appId: string;
    buildId: string;
    bucketId: string;
    limit: number;
    offset: number;
    currentCount: number;
    totalCount: number;
}

export interface UpdateAllDevicesPaginationPayload {
    appId: string;
    limit: number;
    offset: number;
    currentCount: number;
    totalCount: number;
}

export interface DemoReleasePayload {
    releaseId: string;
    release: Release;
    releaseSteps: ReleaseStep[];
    rolloutMonitoring?: RolloutMonitoring;
}

export interface DemoAppsPayload {
    apps: AppDemoData[];
    appGroups: AppGroup[];
    releases: DemoReleasePayload[];
    integrationProviders: IntegrationProvider[];
    statistics: Record<string, OrgOverviewStatistic[]>;
    buildDistroDemoData: BuildDistroReducerType;
    stepRunsDemoData: StringKeyedDictionary<StepRun>;
    stepConfigsDemoData?: StringKeyedDictionary<StepConfig>;
    orgDestinations: Record<string, OrgDestinationsResponse>;
    orgInsights?: Record<string, OrgInsightsResponse>;
    summaryPreferences?: Record<string, OrgSummaryPreferences>;
    summaryViewByRequestId?: Record<string, SummaryViewResponseAPI>;
    orgUsersDemoData?: StringKeyedDictionary<OrgUser>;
}

interface SetDemoReleasesPayload {
    demoReleases: DemoReleasePayload[];
}

interface ClearDemoModeForReleasesPayload {
    releaseIds: string[];
}

export interface UpdateReleasesPaginationAction {
    type: typeof UPDATE_RELEASES_PAGINATION;
    payload: UpdateReleasesPaginationPayload;
}

export interface UpdateBucketBuildsPaginationAction {
    type: typeof UPDATE_BUCKET_BUILDS_PAGINATION;
    payload: UpdateBucketBuildsPaginationPayload;
}

export interface UpdateSharedWithMeBuildsPaginationAction {
    type: typeof UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION;
    payload: UpdateSharedWithMeBuildsPaginationPayload;
}

export interface UpdateBucketBuildWorkItemsTimelinePaginationAction {
    type: typeof UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION;
    payload: UpdateBucketBuildWorkItemsTimelinePaginationPayload;
}

export interface UpdateAllDevicesPaginationAction {
    type: typeof UPDATE_ALL_DEVICES_PAGINATION;
    payload: UpdateAllDevicesPaginationPayload;
}

export interface UpdatePaginationDataAction extends CommonSagaActionsData {
    type: typeof UPDATE_PAGINATION_DATA;
}

export interface SetDemoReleaseAction {
    type: typeof SET_DEMO_RELEASES;
    payload: SetDemoReleasesPayload;
}

export interface ClearDemoModeForReleasesAction {
    type: typeof CLEAR_RELEASE_DEMO_MODE;
    payload: ClearDemoModeForReleasesPayload;
}

export interface SetAppsDemoModeAction {
    type: typeof SET_APPS_DEMO_MODE;
    payload: DemoAppsPayload;
}

export interface ClearAppsDemoModeAction {
    type: typeof CLEAR_APPS_DEMO_MODE;
}

export interface SetShowAddAppsAction {
    type: typeof SET_SHOW_WELCOME_MODAL;
    payload: SetWelcomeModalOptions;
}

export interface AppsAddedShouldRedirectAction {
    type: typeof APPS_ADDED_SHOULD_REDIRECT;
    payload: {
        appIdParams: string[];
    };
}

export interface SetEventsTimelineDrawerVisibilityAction {
    type: typeof SET_EVENTS_TIMELINE_DRAWER_VISIBILITY;
    payload: {
        stepRunId: string | null;
        isVisible: boolean;
    };
}

export type UpdateUIConfigActionType =
    | UpdateUIConfigAction
    | SetDemoReleaseAction
    | ClearDemoModeForReleasesAction
    | SetAppsDemoModeAction
    | ClearAppsDemoModeAction
    | SetShowAddAppsAction
    | AppsAddedSuccessAction
    | UpdateReleasesPaginationAction
    | UpdateBucketBuildsPaginationAction
    | UpdateSharedWithMeBuildsPaginationAction
    | UpdateBucketBuildWorkItemsTimelinePaginationAction
    | UpdateIssuesPaginationAction
    | AppsAddedShouldRedirectAction
    | UpdatePaginationDataAction
    | UpdateUIConfigWebsocketsEnabledAction
    | SetEventsTimelineDrawerVisibilityAction;

// Action Creators

export const appsAddedShouldRedirect: (appIdParams: string[]) => AppsAddedShouldRedirectAction = (appIdParams) => {
    return {
        type: APPS_ADDED_SHOULD_REDIRECT,
        payload: {
            appIdParams,
        },
    };
};

export const updateIsRunwayAdminLiveModeEnabled: (enabled: boolean) => UpdateUIConfigAction = (enabled) => {
    return {
        type: UPDATE_IS_RUNWAY_ADMIN_LIVE_MODE_ENABLED,
        payload: {
            enabled,
        },
    };
};

export const updateIsRunwayAdminWebsocketEnabled: (enabled: boolean) => UpdateUIConfigWebsocketsEnabledAction = (
    enabled,
) => {
    return {
        type: UPDATE_IS_RUNWAY_ADMIN_WEBSOCKETS_ENABLED,
        payload: {
            enabled,
        },
    };
};

export const setDemoReleases: (payload: SetDemoReleasesPayload) => SetDemoReleaseAction = (payload) => {
    return {
        type: SET_DEMO_RELEASES,
        payload,
    };
};

export const clearDemoModeForReleases: (payload: ClearDemoModeForReleasesPayload) => ClearDemoModeForReleasesAction = (
    payload,
) => {
    return {
        type: CLEAR_RELEASE_DEMO_MODE,
        payload,
    };
};

export const setAppsDemoModeData: (payload: DemoAppsPayload) => SetAppsDemoModeAction = (payload) => {
    return {
        type: SET_APPS_DEMO_MODE,
        payload,
    };
};

export const clearAppsDemoMode: () => ClearAppsDemoModeAction = () => {
    return {
        type: CLEAR_APPS_DEMO_MODE,
    };
};

export const setWelcomeModalOptions: (payload: SetWelcomeModalOptions) => SetShowAddAppsAction = (payload) => {
    return {
        type: SET_SHOW_WELCOME_MODAL,
        payload,
    };
};

export const updateReleasesPagination: (payload: UpdateReleasesPaginationPayload) => UpdateReleasesPaginationAction = (
    payload,
) => {
    return {
        type: UPDATE_RELEASES_PAGINATION,
        payload,
    };
};

export const updateBucketBuildsPagination: (
    payload: UpdateBucketBuildsPaginationPayload,
) => UpdateBucketBuildsPaginationAction = (payload) => {
    return {
        type: UPDATE_BUCKET_BUILDS_PAGINATION,
        payload,
    };
};

export const updateSharedWithMeBuildsPagination: (
    payload: UpdateSharedWithMeBuildsPaginationPayload,
) => UpdateSharedWithMeBuildsPaginationAction = (payload) => {
    return {
        type: UPDATE_SHARED_WITH_ME_BUILDS_PAGINATION,
        payload,
    };
};

export const updateBucketBuildWorkItemsTimelinePagination: (
    payload: UpdateBucketBuildWorkItemsTimelinePaginationPayload,
) => UpdateBucketBuildWorkItemsTimelinePaginationAction = (payload) => {
    return {
        type: UPDATE_BUCKET_BUILD_WORK_ITEMS_TIMELINE_PAGINATION,
        payload,
    };
};

export const updateAllDevicesPagination: (
    payload: UpdateAllDevicesPaginationPayload,
) => UpdateAllDevicesPaginationAction = (payload) => {
    return {
        type: UPDATE_ALL_DEVICES_PAGINATION,
        payload,
    };
};

export const updatePaginationDataAction = (actionData: CommonSagaActionsData): UpdatePaginationDataAction => {
    return {
        ...actionData,
        type: UPDATE_PAGINATION_DATA,
    };
};

export const setEventsTimelineDrawerVisibility: (
    stepRunId: string | null,
    isVisible: boolean,
) => SetEventsTimelineDrawerVisibilityAction = (stepRunId, isVisible) => {
    return {
        type: SET_EVENTS_TIMELINE_DRAWER_VISIBILITY,
        payload: {
            stepRunId,
            isVisible,
        },
    };
};
